import { apiUrl, getToken } from './api';

// Client-side errors and playback quality, batched to the server's event log so problems on
// someone else's screen (Tokyo, Delhi…) can be inspected later in the Logs page.

interface TEvent {
  level: 'debug' | 'info' | 'warn' | 'error';
  cat: string;
  msg: string;
  fields?: Record<string, unknown>;
}

let queue: TEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
const seen = new Map<string, number>();

export function report(level: TEvent['level'], cat: string, msg: string, fields?: Record<string, unknown>, opts?: { dedupe?: boolean }) {
  if (!getToken()) return;
  // De-duplicate bursts of the same message (e.g. a stall loop) to one per 30 s, except periodic
  // samples that are meant to repeat.
  if (opts?.dedupe !== false) {
    const key = `${cat}:${msg}`;
    const last = seen.get(key) ?? 0;
    if (Date.now() - last < 30_000) return;
    seen.set(key, Date.now());
  }
  queue.push({ level, cat, msg: msg.slice(0, 500), fields: { ...fields, page: location.pathname } });
  if (queue.length >= 20) flush();
  else if (!timer) timer = setTimeout(flush, 8000);
}

export function flush() {
  clearTimeout(timer);
  timer = undefined;
  if (!queue.length) return;
  const events = queue.splice(0, 40);
  const token = getToken();
  if (!token) return;
  fetch(apiUrl('/api/telemetry'), {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ events }),
  }).catch(() => undefined);
}

export function installGlobalHandlers() {
  window.addEventListener('error', (e) => {
    report('error', 'app', e.message || 'script error', { src: e.filename, line: e.lineno, col: e.colno });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { message?: string; status?: number } | undefined;
    if (r && typeof r.status === 'number' && r.status > 0 && r.status < 500) return; // expected API answers
    report('error', 'app', `unhandled: ${r?.message ?? String(e.reason)}`);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
